import axios, { AxiosError, AxiosInstance } from 'axios';
import { getNodeApiBaseUrl } from '../nodeApiBaseUrl.ts';
import { API_BASE_URL as API_BASE_URL_FROM_ENV } from './flashbackEnv.ts';
import { tokenManager } from '../../utils/tokenManager.js';

declare global {
  interface Window {
    /** Set from `public/runtime-config.js` to override API origin without rebuilding. */
    __NVR_PUBLIC_CONFIG__?: {
      API_BASE_URL?: string;
      VITE_API_BASE_URL?: string;
      FLASHBACK_DUMMY_VIDEO?: boolean | string;
      FLASHBACK_DUMMY_VIDEO_URL?: string;
    };
  }
}

function normalizeApiBaseUrl(raw: string): string {
  const t = raw.trim();
  if (t === '/' || t === '') return '/';
  return t.replace(/\/+$/, '');
}

/**
 * Resolves Axios `baseURL` for Flashback:
 * 1. `window.__NVR_PUBLIC_CONFIG__.API_BASE_URL` (runtime override)
 * 2. `.env` `API_BASE_URL` → synced `flashbackEnv.API_BASE_URL`
 * 3. `REACT_APP_NODE_API_URL` (shared Node Adonis fallback)
 * 4. `window.__NVR_PUBLIC_CONFIG__.VITE_API_BASE_URL` (legacy runtime)
 * 5. `/` (same origin as the SPA / CRA proxy)
 */
function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const runtimePrimary = window.__NVR_PUBLIC_CONFIG__?.API_BASE_URL;
    if (typeof runtimePrimary === 'string' && runtimePrimary.trim() !== '') {
      return normalizeApiBaseUrl(runtimePrimary);
    }
  }

  if (typeof API_BASE_URL_FROM_ENV === 'string' && API_BASE_URL_FROM_ENV.trim() !== '') {
    return normalizeApiBaseUrl(API_BASE_URL_FROM_ENV);
  }

  const fromNodeEnv = getNodeApiBaseUrl();
  if (fromNodeEnv) {
    return normalizeApiBaseUrl(fromNodeEnv);
  }

  if (typeof window !== 'undefined') {
    const runtimeLegacy = window.__NVR_PUBLIC_CONFIG__?.VITE_API_BASE_URL;
    if (typeof runtimeLegacy === 'string' && runtimeLegacy.trim() !== '') {
      return normalizeApiBaseUrl(runtimeLegacy);
    }
  }

  return '/';
}

const baseURL = resolveApiBaseUrl();

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

function browserIanaTimeZone(): string | undefined {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (typeof tz === 'string' && tz.length > 0 && tz.length <= 64) return tz;
  } catch {
    /* ignore */
  }
  return undefined;
}

http.interceptors.request.use((config) => {
  const tz = browserIanaTimeZone();
  if (tz) {
    config.headers.set('X-Client-Timezone', tz);
  }
  const token = tokenManager.getToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

export class HttpError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function extractApiError(raw: unknown): ApiError | undefined {
  if (!raw || typeof raw !== 'object' || raw instanceof Blob) return undefined;
  const o = raw as Record<string, unknown>;
  if (o.error && typeof o.error === 'object') {
    const err = o.error as Record<string, unknown>;
    return {
      code: typeof err.code === 'string' ? err.code : 'REQUEST_FAILED',
      message:
        (typeof err.message === 'string' && err.message) ||
        (typeof o.message === 'string' && o.message) ||
        'Request failed',
      details: err.details,
    };
  }
  if (typeof o.message === 'string') {
    return {
      code: typeof o.code === 'string' ? o.code : 'REQUEST_FAILED',
      message: o.message,
    };
  }
  return undefined;
}

http.interceptors.response.use(
  (res) => res,
  async (error: unknown) => {
    if (axios.isCancel(error)) return Promise.reject(error);
    const err = error as AxiosError;
    const status = err.response?.status ?? 0;
    let raw = err.response?.data;

    if (raw instanceof Blob) {
      try {
        raw = JSON.parse(await raw.text()) as unknown;
      } catch {
        /* ignore */
      }
    }

    const apiErr = extractApiError(raw);

    throw new HttpError(
      apiErr?.message ?? err.message ?? 'Request failed',
      status,
      apiErr?.code ?? 'NETWORK_ERROR',
      apiErr?.details,
    );
  },
);
